import { useCallback, useRef } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { ProvedorAvisos } from '@du/ui'
import { Layout } from './componentes/Layout.js'
import { useSessao } from './lib/sessao.js'
import { useEventos, useSomPedido, type EventoSse } from './lib/tempoReal.js'
import { Cardapio } from './paginas/Cardapio.js'
import { Clientes } from './paginas/Clientes.js'
import { Historico } from './paginas/Historico.js'
import { DesignSystem } from './paginas/DesignSystem.js'
import { Impressao } from './paginas/Impressao.js'
import { Dashboard } from './paginas/Dashboard.js'
import { Kanban } from './paginas/Kanban.js'
import { Motoboys } from './paginas/Motoboys.js'
import { Loja } from './paginas/Loja.js'
import { Login } from './paginas/Login.js'
import { Whatsapp } from './paginas/Whatsapp.js'

function Portal({ som }: { som: ReturnType<typeof useSomPedido> }) {
  // Ouvintes num ref: registrar num estado faria o useEventos reassinar o SSE
  // a cada render, e o fluxo reconectaria sem parar.
  const ouvintes = useRef(new Set<(e: EventoSse) => void>())

  const registrar = useCallback((fn: (e: EventoSse) => void) => {
    ouvintes.current.clear()
    ouvintes.current.add(fn)
  }, [])

  const { conectado } = useEventos(
    useCallback(
      (e: EventoSse) => {
        /**
         * Som em DOIS momentos, e só nesses dois: pedido novo e pagamento
         * confirmado.
         *
         * O segundo entrou porque o pedido em Pix agora entra ACEITO e
         * imprime quando o dinheiro cai — e isso acontece minutos depois de
         * o pedido chegar, quando ninguém está mais olhando a tela. Sem o
         * bipe, a comanda sai sozinha na impressora e fica lá.
         *
         * Continua sem tocar a cada mudança de status: isso viraria ruído e,
         * em uma semana, alguém desliga a caixa de som — e aí o alerta que
         * importa some junto.
         */
        const pagou =
          e.tipo === 'pedido.pagamento' &&
          (e.dados as { statusPagamento?: string } | undefined)?.statusPagamento === 'PAGO'
        if (e.tipo === 'pedido.criado' || pagou) som.tocar()
        for (const ouvinte of ouvintes.current) ouvinte(e)
      },
      [som],
    ),
  )

  return (
    <Layout conectado={conectado} som={som}>
      <Routes>
        <Route path="/" element={<Kanban registrar={registrar} />} />
        <Route path="/cardapio" element={<Cardapio />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/historico" element={<Historico />} />
        <Route path="/vendas" element={<Dashboard />} />
        <Route path="/motoboys" element={<Motoboys />} />
        <Route path="/loja" element={<Loja />} />
        <Route path="/impressoras" element={<Impressao registrar={registrar} />} />
        <Route path="/whatsapp" element={<Whatsapp registrar={registrar} />} />
        <Route path="/design-system" element={<DesignSystem />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  )
}

export function App() {
  const { usuario, carregando } = useSessao()
  const som = useSomPedido()

  if (carregando) return <div className="carregando">Carregando…</div>

  return (
    <ProvedorAvisos>
      {usuario ? <Portal som={som} /> : <Login aoAtivarSom={som.ativar} />}
    </ProvedorAvisos>
  )
}
